import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/format'

interface PostMetaProps {
    postId: number
    authorName: string
    publishedAt: string
    /**
     * Stamped by the posts table's BEFORE UPDATE trigger, so it cannot drift away
     * from the content. Shown only when the piece has genuinely been revised since
     * publication: an "updated" date that matches the publish date is noise.
     */
    updatedAt?: string
    readMinutes: number
    className?: string
}

const DAY = 24 * 60 * 60 * 1000

export const PostMeta: FC<PostMetaProps> = ({
    postId,
    authorName,
    publishedAt,
    updatedAt,
    readMinutes,
    className
}): ReactNode => {
    const revised =
        updatedAt && new Date(updatedAt).getTime() - new Date(publishedAt).getTime() > DAY
            ? updatedAt
            : null

    return (
        <div className={cn('flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground', className)}>
            <span className='font-medium text-foreground/80' data-mdlfy-cms={`posts:${postId}:author_name`}>
                {authorName}
            </span>
            <span aria-hidden='true'>·</span>
            {revised ? (
                <span>
                    Updated <time dateTime={revised}>{formatDate(revised)}</time>
                </span>
            ) : (
                <time dateTime={publishedAt}>{formatDate(publishedAt)}</time>
            )}
            <span aria-hidden='true'>·</span>
            <span>
                <span>{readMinutes}</span> min read
            </span>
        </div>
    )
}
